import * as React from "react";
import * as S from "@styled";
import { useDispatch, useSelector } from "react-redux";
import { updateParameter } from "../actions/UpdateParameter";
import { UPDATE_SELECTED_TYPE, UpdateSelectedTypeAction } from "../actions/UpdateSelectedType";
import { IElement, IParameterSchema, SharedStateMethods } from "../model/IProcessData";
import { IValueLabel } from "../model/ITriggerData";
import { State } from "../model/State";
import { DropdownInput } from "./DropdownInput";
import LogResultEditor from "./LogResultEditor";
import ParameterInput from "./ParameterInput";
import PromoteResultEditor from "./PromoteResultEditor";
import { SwitchCollapse } from "./SwitchCollapse";
import TriggerInput, { InputMode } from "./Triggers/TriggerInput";
import TypeSelector from "./TypeSelector";
import { ParameterEditorValue } from "../model/ParameterEditorValue";

const parameterSchema: IParameterSchema = {
    inputType: "expression",
    type: "dynamic",
    name: "Key",
    parameterType: "TextParameter",
    defaultValue: "",
    isSecret: false
};

const ttlParameterSchema: IParameterSchema = {
    inputType: "number",
    type: "dynamic",
    name: "TTL",
    description: "After this duration the key will be deleted from the shared store",
    parameterType: "TextParameter",
    defaultValue: { mode: "integer", value: 60 },
    minimum: 0,
    isSecret: false
};

const isTransformRequired = (parameter: ParameterEditorValue | number): boolean => {
    if (parameter && (typeof parameter === "number" || parameter instanceof Number)) {
        return true;
    }
    return false;
};

const methodSelectorModel = Object.values(SharedStateMethods).map((x) => ({ label: x, value: x }));
export const SharedStateEditor: React.FC<SharedStateEditorProps> = (props) => {
    const element = useSelector<State, IElement>((state) => state.elements[props.id]);
    const disableEdit = useSelector<State, boolean | undefined>((state) => state.uiState.disableEdit);
    const dispatch = useDispatch();
    if (!element) {
        return null;
    }
    const hideInputsForNonUpdates = element.selectedTypeId === SharedStateMethods.TryGetValue || element.selectedTypeId === SharedStateMethods.GetTTL ||
        element.selectedTypeId === SharedStateMethods.TryRemove;

    const isAdvancedPanelOpen = element.shouldNotLogResult ||
        (Object.prototype.hasOwnProperty.call(element, "promoteResultAs") && element.promoteResultAs !== null);

    const handleTypeChange = (typeId: string) => {
        dispatch<UpdateSelectedTypeAction>({
            type: UPDATE_SELECTED_TYPE,
            elementId: element.id,
            selectedTypeId: typeId,
            defaultParameters: {
                keyExpression: element.parameters["keyExpression"],
                // Lets reset this here so that i case of type change and a old #ref we wont get compilation issues
                valueExpression: hideInputsForNonUpdates ? element.parameters["valueExpression"] : { mode: "csharp", value: "" },
                ttlValue: element.parameters["ttlValue"],
                ttlMultiplier: element.parameters["ttlMultiplier"],
                throwIfFalse: element.parameters["throwIfFalse"],
                isGlobalScope: element.parameters["isGlobalScope"]
            }
        });
    };

    const handleTimeModeChange = (value: string) => {
        const newValue = parseInt(value, 10);
        dispatch(updateParameter(element.id, "ttlMultiplier", newValue) as any);
    };

    // ACC-8500. Support pre-expression (i.e before v5.6) values
    const ttlParameterValue = element.parameters["ttlValue"];
    if (isTransformRequired(ttlParameterValue)) {
        element.parameters["ttlValue"] = { mode: "integer", value: ttlParameterValue };
    }

    return <>
        <SwitchCollapse
            isOpenByDefault={isAdvancedPanelOpen}
            childrenCollapsed={null}
            childrenOpen={
                <div>
                    <LogResultEditor elementId={props.id} />
                    <PromoteResultEditor id={props.id} />
                </div>
            }
        />
        <TypeSelector
            selectorValuesModel={methodSelectorModel}
            selectorModel={methodSelectorModel}
            id={props.id}
            selectedTypeId={element.selectedTypeId}
            onChange={handleTypeChange}
        />
        <hr />
        <ParameterInput
            key="keyExpression"
            name="keyExpression"
            elementId={props.id}
            label="Key"
            value={element.parameters["keyExpression"]}
            parameterSchema={parameterSchema}
        />

        {hideInputsForNonUpdates ? null :
            <>
                {element.selectedTypeId === SharedStateMethods.UpdateTTL ? null : <ParameterInput
                    key="valueExpression"
                    name="valueExpression"
                    elementId={props.id}
                    label="Value"
                    value={element.parameters["valueExpression"]}
                    parameterSchema={parameterSchema}
                />}
                <S.FormGroup>
                    <S.FormGroup>
                        <ParameterInput
                            key="ttlValue"
                            name="ttlValue"
                            label="Time to live"
                            value={element.parameters["ttlValue"]}
                            elementId={props.id}
                            parameterSchema={ttlParameterSchema}
                            min={0}
                        />
                        <DropdownInput
                            extraClasses="col-xs-4 no-padding"
                            dropdownModel={TtlMultipliers}
                            elementId={props.id}
                            key="ttlMultiplier"
                            identifier="ttlMultiplier"
                            disabled={disableEdit}
                            existingParameters={element.parameters}
                            onChange={handleTimeModeChange}
                            label={null}
                        />
                    </S.FormGroup>
                </S.FormGroup>
            </>
        }

        <TriggerInput
            elementId={props.id}
            name="throwIfFalse"
            existingParameters={element.parameters}
            inputMode={InputMode.boolean}
            label="Throw an exception on failure"
            description={"If set the process will throw en exception if the shared state call fails for some reason."}
        />
        <TriggerInput
            key="isGlobalScope"
            name="isGlobalScope"
            elementId={props.id}
            existingParameters={element.parameters}
            inputMode={InputMode.boolean}
            label="Available for all Processes in Agent Group"
            description={"If set the key:value will be available for all processes in the Agent Group"}
        />
    </>;
};

interface SharedStateEditorProps {
    id: string;
    existingParameters: any;
}

const TtlMultipliers: IValueLabel[] = [
    { value: 60, label: FRENDS.Localizations.Minutes },
    { value: 3600, label: FRENDS.Localizations.Hours }
];
